import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import WarningModal from "components/DeleteModal/WarningModal";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import { formatDate, useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaExclamationTriangle } from "react-icons/fa";
import { deleteReservationFeedback, getAllFeedbacksByUserId } from "redux/features/reservations/reservationSlice";
import Input from "shared/Input/Input";
import CommonLayout from "../AccountPage/CommonLayout";
import ReclamationReservationDetails from "./ReclamationReservationDetails";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useNavigate } from "react-router-dom";

export interface ReservationsFeedbackProps {
  className?: string;
}

interface Feedback {
  id: number;
  reference: string;
  sujet?: string;
  description: string;
  clientId?: number;
  ownerId?: number;
  status: "pending" | "released";
  enable?: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface ModalState {
  isViewModalOpen: boolean;
  isDeleteModalOpen: boolean;
  selectedItem: Feedback | null;
  loading: boolean;
}

const ReservationsFeedback: FC<ReservationsFeedbackProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    feedbacks: feedbackList,
    totalCount,
    totalPages,
    currentPage,
    loading,
  } = useAppSelector((state) => ({
    feedbacks: state.featureReservation?.feedbacks || [],
    totalCount: state.featureReservation?.totalCount || 0,
    totalPages: state.featureReservation?.totalPages || 0,
    currentPage: state.featureReservation?.currentPage || 1,
    loading: state.featureReservation?.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isDeleteModalOpen: false,
    isViewModalOpen: false,
    selectedItem: null,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(getAllFeedbacksByUserId({ page, reference: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleDeleteModalOpen = (feedback: Feedback) => {
    setModalState({ ...modalState, selectedItem: feedback, isDeleteModalOpen: true });
  };

  const handleViewModalOpen = (feedback: Feedback) => {
    setModalState({ ...modalState, selectedItem: feedback, isViewModalOpen: true });
  };

  const handleModalClose = () => {
    setModalState({
      ...modalState,
      isDeleteModalOpen: false,
      isViewModalOpen: false,
      selectedItem: null,
    });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteReservationFeedback(modalState.selectedItem.reference));
      setModalState({
        ...modalState,
        isDeleteModalOpen: false,
        loading: false,
        selectedItem: null,
      });
      await dispatch(getAllFeedbacksByUserId({ page, reference: debouncedSearchTerm }));
    }
  };

  const handleTableAction = (action: string, item: Feedback) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "view") {
      handleViewModalOpen(item);
    }
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      render: (item: Feedback) => <span className="align-middle">{formatDate(item.createdAt)}</span>,
    },
    {
      key: "reference",
      label: "code réservation",
      render: (item: Feedback) => <span className="align-middle">{item.reference}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (item: Feedback) => (
        <span
          className={`inline-block px-2 py-1 text-xs font-semibold rounded-full align-middle ${
            item.status === "pending"
              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
          }`}
        >
          {item.status === "pending" ? "En cours" : "Libéré"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];
  const navigate = useNavigate();
  return (
    <div
      className={`min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900 ${className}`}
      data-nc-id="AccountPage"
    >
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <CommonLayout>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaExclamationTriangle className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Réclamations
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary
                onClick={() => navigate("/client/ajouter-reclamations-reservations")}
                className="btn-primary-gradient"
                sizeClass="px-4 py-2"
              >
                Ajouter une réclamation
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par désignation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search categories"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {modalState.isDeleteModalOpen && (
                <WarningModal
                  open={modalState.isDeleteModalOpen}
                  id={modalState.selectedItem?.id || 0}
                  title={modalState.selectedItem?.reference || ""}
                  handleClose={handleModalClose}
                  handleDelete={handleDeleteConfirm}
                  isLoading={modalState.loading}
                  alertMsg="Êtes-vous sûr de libérer le paiement ?"
                  msg="Confirmez-vous la libération du paiement de la réservation sous la référence"
                />
              )}

              {modalState.isViewModalOpen && (
                <ReclamationReservationDetails
                  open={modalState.isViewModalOpen}
                  handleClose={handleModalClose}
                  data={modalState.selectedItem}
                />
              )}

              <DynamicTable
                columns={columns}
                data={feedbackList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view"]}
              />
            </>
          )}
        </div>
      </CommonLayout>
    </div>
  );
};

export default ReservationsFeedback;
